/**
 * templateWizardPresets.js
 *
 * Registry of wizard presets for creating Action Item templates.
 * Each preset defines a module context and a set of well-known fields that the
 * backend will auto-populate when creating action items from that module.
 *
 * HOW TO ADD A NEW PRESET
 * -----------------------
 * 1. Add a new entry to WIZARD_PRESETS below.
 * 2. Define all fields that the backend populates in DataJson.
 * 3. Choose appropriate field types and layout sections.
 * 4. Ensure the `moduleKey` / `contextKey` pair matches the PREDEFINED_CONTEXTS
 *    in ModuleTemplateConfigPage and the backend code that creates action items.
 * 5. Optionally add suggested metadata (defaultPriority, defaultDueDays, etc.).
 */

import {
    FaCogs, FaWrench, FaNetworkWired, FaExclamationTriangle,
    FaServer, FaBuilding, FaTag, FaBarcode, FaMapMarkerAlt,
    FaCalendarAlt, FaMoneyBillWave, FaUser, FaFileInvoice,
    FaClock, FaCheckCircle, FaThermometerHalf, FaWifi,
    FaPlusCircle, FaClipboardCheck, FaTasks, FaUserPlus,
    FaShoppingCart, FaFileSignature, FaHeadset, FaExchangeAlt,
    FaUserCheck, FaUserTimes, FaCode, FaProjectDiagram,
    FaStream, FaClipboardList, FaToolbox, FaFileContract,
    FaHandshake, FaSearch, FaLaptop, FaUserShield
} from 'react-icons/fa';

/**
 * Master list of wizard presets.
 *
 * Each preset contains:
 *  - id            Unique identifier for the preset
 *  - moduleKey     Matches ModuleTemplateAssignment.ModuleKey
 *  - contextKey    Matches ModuleTemplateAssignment.ContextKey
 *  - displayName   Human-readable name shown in the wizard
 *  - description   Short explanation of the preset
 *  - icon          React icon component for the card
 *  - color         Accent colour for the card
 *  - suggestedMeta Default metadata values the user can override
 *  - sections      Array of template sections, each containing fields
 *                  These map directly to the SchemaJson sections format
 *                  used by TemplateDesigner / TemplateRenderer.
 */
export const WIZARD_PRESETS = [
    // ─── AD-HOC ACTION ITEMS ────────────────────────────────────────────────────
    {
        id: 'ad-hoc',
        moduleKey: 'General',
        contextKey: 'AdHoc',
        displayName: 'Ad-Hoc Action Items',
        description: 'General-purpose template for action items created manually from the Action Items page. Use this when you need a flexible template that is not tied to any specific module — ideal for one-off tasks, follow-ups, or cross-department requests.',
        icon: FaPlusCircle,
        color: '#7f8c8d',
        suggestedMeta: {
            templateKeyPrefix: 'General-AdHoc',
            displayNameDefault: 'Ad-Hoc Action Item',
            descriptionDefault: 'General-purpose template for manually created action items',
            defaultPriority: 'Medium',
            defaultDueDays: 7,
            department: '',
            icon: 'fa-plus-circle',
            color: '#7f8c8d',
            tags: 'ad-hoc,manual,general'
        },
        sections: [
            {
                title: 'Task Details',
                columns: 1,
                fields: [
                    { name: 'taskSummary', label: 'Task Summary', type: 'text', required: true, helpText: 'A brief summary of what needs to be done', placeholder: 'e.g. Follow up with tenant regarding complaint' },
                    { name: 'taskDescription', label: 'Full Description', type: 'textarea', required: false, helpText: 'Provide any additional context, background, or instructions for the person completing this task', placeholder: 'Enter detailed description...' },
                    {
                        type: 'row', name: 'row_adhoc_category', label: 'Categorisation',
                        columns: [
                            { span: 6, fields: [{ name: 'category', label: 'Category', type: 'select', required: false, helpText: 'Choose a broad category to help organise and filter action items', options: ['General', 'Maintenance', 'Tenant', 'Compliance', 'Administrative', 'Financial', 'Safety', 'Other'] }] },
                            { span: 6, fields: [{ name: 'urgency', label: 'Urgency', type: 'select', required: false, helpText: 'How urgent is this task? This helps the assigned person prioritise their work', options: ['Low', 'Medium', 'High', 'Critical'] }] }
                        ]
                    }
                ]
            },
            {
                title: 'Location & Context',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_adhoc_location', label: 'Location',
                        columns: [
                            { span: 6, fields: [{ name: 'propertyName', label: 'Property', type: 'text', required: false, helpText: 'The property this task relates to (if applicable)', placeholder: 'Auto-populated if selected' }] },
                            { span: 6, fields: [{ name: 'areaOrUnit', label: 'Area / Unit', type: 'text', required: false, helpText: 'Specific area, floor, or unit number within the property', placeholder: 'e.g. Building A, Unit 5' }] }
                        ]
                    },
                    { name: 'referenceInfo', label: 'Reference Information', type: 'text', required: false, helpText: 'Any external reference number, email subject, or related document that gives context to this task', placeholder: 'e.g. Email from John Smith dated 15 Jan' }
                ]
            }
        ]
    },

    // ─── SIPA INSPECTIONS ───────────────────────────────────────────────────────
    {
        id: 'sipa-inspections',
        moduleKey: 'Operations',
        contextKey: 'SIPA',
        displayName: 'SIPA Inspections',
        description: 'Template for action items generated from SIPA (Standard Inspection Property Assessment) sub-section findings. When an inspector flags a problem during a property walkthrough, the system creates an action item with the inspection details already filled in.',
        icon: FaClipboardCheck,
        color: '#2c3e50',
        suggestedMeta: {
            templateKeyPrefix: 'Operations-SIPA',
            displayNameDefault: 'SIPA Inspection Action Item',
            descriptionDefault: 'Template for SIPA inspection finding action items',
            defaultPriority: 'Medium',
            defaultDueDays: 14,
            department: 'Operations',
            icon: 'fa-clipboard-check',
            color: '#2c3e50',
            tags: 'sipa,inspection,property,assessment'
        },
        sections: [
            {
                title: 'Inspection Finding',
                columns: 1,
                fields: [
                    { name: 'findingDescription', label: 'Finding Description', type: 'textarea', required: true, helpText: 'What was found during the inspection — this is automatically populated from the SIPA sub-section the inspector flagged', placeholder: 'Auto-populated from SIPA report' },
                    {
                        type: 'row', name: 'row_sipa_section', label: 'Report Section',
                        columns: [
                            { span: 6, fields: [{ name: 'sectionName', label: 'Section Name', type: 'text', required: false, helpText: 'The SIPA report section this finding belongs to (e.g. Exterior, Common Areas, Parking)', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'subSectionName', label: 'Sub-Section', type: 'text', required: false, helpText: 'The specific sub-section within the report section', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    {
                        type: 'row', name: 'row_sipa_priority', label: 'Classification',
                        columns: [
                            { span: 6, fields: [{ name: 'findingPriority', label: 'Finding Priority', type: 'select', required: false, helpText: 'How critical is this finding? Critical items may pose a safety risk', options: ['Low', 'Medium', 'High', 'Critical'] }] },
                            { span: 6, fields: [{ name: 'findingType', label: 'Finding Type', type: 'select', required: false, helpText: 'What kind of issue was identified', options: ['Maintenance Required', 'Safety Hazard', 'Cosmetic', 'Structural', 'Cleaning', 'Signage', 'Landscaping', 'Other'] }] }
                        ]
                    }
                ]
            },
            {
                title: 'Property & Location',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_sipa_property', label: 'Property',
                        columns: [
                            { span: 6, fields: [{ name: 'propertyName', label: 'Property', type: 'text', required: false, helpText: 'The property where the inspection was conducted', placeholder: 'Auto-populated from SIPA report' }] },
                            { span: 6, fields: [{ name: 'inspectionDate', label: 'Inspection Date', type: 'date', required: false, helpText: 'The date the SIPA inspection was performed', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'specificLocation', label: 'Specific Location', type: 'text', required: false, helpText: 'Exact location of the finding within the property (e.g. "North entrance, ground floor corridor")', placeholder: 'Auto-populated if captured' }
                ]
            },
            {
                title: 'Resolution',
                columns: 1,
                fields: [
                    { name: 'correctiveAction', label: 'Corrective Action Required', type: 'textarea', required: false, helpText: 'Describe what needs to be done to resolve this finding', placeholder: 'Enter the action that needs to be taken...' },
                    { name: 'resolutionNotes', label: 'Resolution Notes', type: 'textarea', required: false, helpText: 'Notes about how the issue was resolved (completed when the action item is closed)', placeholder: 'Enter resolution details when complete...' }
                ]
            }
        ]
    },

    // ─── NON-COMPLIANCE REPORTS ─────────────────────────────────────────────────
    {
        id: 'non-compliance',
        moduleKey: 'Operations',
        contextKey: 'NonCompliance',
        displayName: 'Non-Compliance Reports',
        description: 'Template for action items raised when a tenant is found to be in breach of their lease agreement. Common non-compliance issues include unauthorised signage, safety violations, operating hour breaches, or building alterations without approval.',
        icon: FaExclamationTriangle,
        color: '#e74c3c',
        suggestedMeta: {
            templateKeyPrefix: 'Operations-NonCompliance',
            displayNameDefault: 'Non-Compliance Action Item',
            descriptionDefault: 'Template for tenant non-compliance action items',
            defaultPriority: 'High',
            defaultDueDays: 7,
            department: 'Operations',
            icon: 'fa-exclamation-triangle',
            color: '#e74c3c',
            tags: 'non-compliance,tenant,lease,violation'
        },
        sections: [
            {
                title: 'Non-Compliance Details',
                columns: 1,
                fields: [
                    { name: 'nonComplianceType', label: 'Non-Compliance Type', type: 'text', required: true, helpText: 'The type of non-compliance issue (e.g. "Unauthorised Signage", "Safety Violation"). This is automatically populated from the non-compliance report.', placeholder: 'Auto-populated from report' },
                    { name: 'description', label: 'Description', type: 'textarea', required: false, helpText: 'Full details of the non-compliance issue and the relevant lease clause being breached', placeholder: 'Auto-populated from report' },
                    { name: 'comments', label: 'Additional Comments', type: 'textarea', required: false, helpText: 'Any additional notes from the person who raised the non-compliance report', placeholder: 'Auto-populated' }
                ]
            },
            {
                title: 'Tenant & Property',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_nc_tenant', label: 'Tenant Info',
                        columns: [
                            { span: 6, fields: [{ name: 'tenantName', label: 'Tenant Name', type: 'text', required: false, helpText: 'The name of the tenant involved in the non-compliance', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'shopNumber', label: 'Shop / Unit Number', type: 'text', required: false, helpText: 'The shop or unit number of the tenant', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'propertyName', label: 'Property', type: 'text', required: false, helpText: 'The property where the non-compliance was observed', placeholder: 'Auto-populated' }
                ]
            },
            {
                title: 'Tenant Contact & Response',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_nc_contact', label: 'Tenant Contact',
                        columns: [
                            { span: 4, fields: [{ name: 'tenantContactName', label: 'Contact Name', type: 'text', required: false, helpText: 'Name of the tenant representative contacted', placeholder: 'Auto-populated' }] },
                            { span: 4, fields: [{ name: 'tenantContactNumber', label: 'Contact Number', type: 'text', required: false, helpText: 'Phone number of the tenant contact', placeholder: 'Auto-populated' }] },
                            { span: 4, fields: [{ name: 'tenantContactEmail', label: 'Contact Email', type: 'text', required: false, helpText: 'Email address of the tenant contact', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'tenantResponse', label: 'Tenant Response', type: 'textarea', required: false, helpText: 'The tenant\'s response or explanation regarding the non-compliance', placeholder: 'Record tenant\'s response here...' }
                ]
            }
        ]
    },

    // ─── CM DAILY CHECKLIST ─────────────────────────────────────────────────────
    {
        id: 'cm-daily-checklist',
        moduleKey: 'Operations',
        contextKey: 'CMDailyChecklist',
        displayName: 'CM Daily Checklist',
        description: 'Template for action items created from Centre Manager daily property checklists. When a Centre Manager identifies an issue during their daily walkthrough, the system creates an action item with the checklist details already attached.',
        icon: FaTasks,
        color: '#27ae60',
        suggestedMeta: {
            templateKeyPrefix: 'Operations-CMDailyChecklist',
            displayNameDefault: 'CM Daily Checklist Action Item',
            descriptionDefault: 'Template for Centre Manager daily checklist action items',
            defaultPriority: 'Medium',
            defaultDueDays: 3,
            department: 'Operations',
            icon: 'fa-tasks',
            color: '#27ae60',
            tags: 'checklist,daily,centre-manager,property'
        },
        sections: [
            {
                title: 'Checklist Finding',
                columns: 1,
                fields: [
                    { name: 'checklistItem', label: 'Checklist Item', type: 'text', required: true, helpText: 'The specific checklist item that was flagged — this is auto-populated from the daily checklist form', placeholder: 'Auto-populated from checklist' },
                    { name: 'findingDescription', label: 'Finding Description', type: 'textarea', required: false, helpText: 'Description of what was found during the walkthrough. Include details like the exact location and condition observed.', placeholder: 'Auto-populated from checklist' },
                    {
                        type: 'row', name: 'row_cm_category', label: 'Classification',
                        columns: [
                            { span: 6, fields: [{ name: 'checklistSection', label: 'Checklist Section', type: 'text', required: false, helpText: 'The section of the daily checklist this item came from (e.g. Parking, Common Areas, Ablutions)', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'severity', label: 'Severity', type: 'select', required: false, helpText: 'How severe is the issue? This helps prioritise the response', options: ['Minor', 'Moderate', 'Major', 'Critical'] }] }
                        ]
                    }
                ]
            },
            {
                title: 'Property & Walkthrough',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_cm_property', label: 'Property',
                        columns: [
                            { span: 6, fields: [{ name: 'propertyName', label: 'Property', type: 'text', required: false, helpText: 'The property where the daily checklist was performed', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'walkthroughDate', label: 'Walkthrough Date', type: 'date', required: false, helpText: 'The date the daily walkthrough was performed', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'specificLocation', label: 'Specific Location', type: 'text', required: false, helpText: 'Where exactly in the property was the issue found (e.g. "Ground floor lobby, near lifts")', placeholder: 'Enter location details' }
                ]
            },
            {
                title: 'Action Required',
                columns: 1,
                fields: [
                    { name: 'actionRequired', label: 'Action Required', type: 'textarea', required: false, helpText: 'What needs to be done to address this finding', placeholder: 'Describe the corrective action needed...' },
                    { name: 'resolutionNotes', label: 'Resolution Notes', type: 'textarea', required: false, helpText: 'Notes about how the issue was resolved (fill in when closing the action item)', placeholder: 'Enter resolution details when complete...' }
                ]
            }
        ]
    },

    // ─── ROM DAILY CHECKLIST ────────────────────────────────────────────────────
    {
        id: 'rom-daily-checklist',
        moduleKey: 'Operations',
        contextKey: 'ROMDailyChecklist',
        displayName: 'ROM Daily Checklist',
        description: 'Template for action items from Regional Operations Manager daily oversight checklists. Similar to the CM Daily Checklist but focused on regional-level oversight across multiple properties, ensuring Centre Managers are meeting their operational standards.',
        icon: FaClipboardList,
        color: '#16a085',
        suggestedMeta: {
            templateKeyPrefix: 'Operations-ROMDailyChecklist',
            displayNameDefault: 'ROM Daily Checklist Action Item',
            descriptionDefault: 'Template for Regional Operations Manager checklist action items',
            defaultPriority: 'Medium',
            defaultDueDays: 5,
            department: 'Operations',
            icon: 'fa-clipboard-list',
            color: '#16a085',
            tags: 'checklist,daily,rom,regional,oversight'
        },
        sections: [
            {
                title: 'Oversight Finding',
                columns: 1,
                fields: [
                    { name: 'checklistItem', label: 'Checklist Item', type: 'text', required: true, helpText: 'The specific oversight checklist item that was flagged during the ROM review', placeholder: 'Auto-populated from checklist' },
                    { name: 'findingDescription', label: 'Finding Description', type: 'textarea', required: false, helpText: 'Detailed description of the finding from the regional oversight walkthrough', placeholder: 'Auto-populated from checklist' },
                    {
                        type: 'row', name: 'row_rom_category', label: 'Classification',
                        columns: [
                            { span: 6, fields: [{ name: 'checklistSection', label: 'Checklist Section', type: 'text', required: false, helpText: 'The section of the ROM daily checklist this finding relates to', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'severity', label: 'Severity', type: 'select', required: false, helpText: 'How severe is this oversight issue?', options: ['Minor', 'Moderate', 'Major', 'Critical'] }] }
                        ]
                    }
                ]
            },
            {
                title: 'Property & Review',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_rom_property', label: 'Property',
                        columns: [
                            { span: 6, fields: [{ name: 'propertyName', label: 'Property', type: 'text', required: false, helpText: 'The property being reviewed during the ROM oversight visit', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'reviewDate', label: 'Review Date', type: 'date', required: false, helpText: 'Date the regional oversight review was conducted', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'centreManager', label: 'Centre Manager', type: 'text', required: false, helpText: 'The Centre Manager responsible for the property being reviewed', placeholder: 'Auto-populated' }
                ]
            },
            {
                title: 'Corrective Action',
                columns: 1,
                fields: [
                    { name: 'actionRequired', label: 'Action Required', type: 'textarea', required: false, helpText: 'What corrective action needs to be taken by the Centre Manager or operations team', placeholder: 'Describe what needs to be addressed...' },
                    { name: 'resolutionNotes', label: 'Resolution Notes', type: 'textarea', required: false, helpText: 'How the issue was resolved (fill in when closing)', placeholder: 'Enter resolution details when complete...' }
                ]
            }
        ]
    },

    // ─── ASSET SERVICE ─────────────────────────────────────────────────────────
    {
        id: 'asset-service',
        moduleKey: 'Operations',
        contextKey: 'AssetService',
        displayName: 'Asset Service',
        description: 'Template for action items created when assets are due for scheduled servicing or require Repairs & Maintenance approval. Fields are auto-populated from the Asset and Service Record data.',
        icon: FaCogs,
        color: '#2980b9',
        suggestedMeta: {
            templateKeyPrefix: 'Operations-AssetService',
            displayNameDefault: 'Asset Service Action Item',
            descriptionDefault: 'Template for asset service and maintenance action items',
            defaultPriority: 'Medium',
            defaultDueDays: 7,
            department: 'Operations',
            icon: 'fa-cogs',
            color: '#2980b9',
            tags: 'asset,service,maintenance'
        },
        sections: [
            {
                title: 'Asset Information',
                columns: 1,
                fields: [
                    { name: 'assetName', label: 'Asset Name', type: 'text', required: true, helpText: 'Name of the asset requiring service', placeholder: 'Auto-populated from asset record' },
                    {
                        type: 'row', name: 'row_asset_ids', label: 'Asset Identifiers',
                        columns: [
                            { span: 6, fields: [{ name: 'assetTag', label: 'Asset Tag', type: 'text', required: false, helpText: 'Asset tag / barcode', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'serialNumber', label: 'Serial Number', type: 'text', required: false, helpText: 'Serial number', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    {
                        type: 'row', name: 'row_asset_class', label: 'Classification',
                        columns: [
                            { span: 6, fields: [{ name: 'assetCategory', label: 'Category', type: 'text', required: false, helpText: 'Asset category name', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'assetSubCategory', label: 'Sub-Category', type: 'text', required: false, helpText: 'Asset sub-category name', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    {
                        type: 'row', name: 'row_asset_make', label: 'Make & Model',
                        columns: [
                            { span: 6, fields: [{ name: 'manufacturer', label: 'Manufacturer', type: 'text', required: false, helpText: 'Manufacturer', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'model', label: 'Model', type: 'text', required: false, helpText: 'Model', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'location', label: 'Location', type: 'text', required: false, helpText: 'Location within the property', placeholder: 'Auto-populated' }
                ]
            },
            {
                title: 'Service Record',
                columns: 1,
                fields: [
                    { name: 'recordType', label: 'Record Type', type: 'text', required: false, helpText: 'ScheduledService or RepairAndMaintenance', placeholder: 'Auto-populated' },
                    { name: 'recordTitle', label: 'Service Title', type: 'text', required: false, helpText: 'Service record title', placeholder: 'Auto-populated' },
                    {
                        type: 'row', name: 'row_service_dates', label: 'Service Dates',
                        columns: [
                            { span: 6, fields: [{ name: 'serviceDueDate', label: 'Due Date', type: 'date', required: false, helpText: 'Scheduled due date', placeholder: '' }] },
                            { span: 6, fields: [{ name: 'serviceDate', label: 'Actual Service Date', type: 'date', required: false, helpText: 'Actual date service was performed', placeholder: '' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Provider & Cost',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_provider', label: 'Provider Info',
                        columns: [
                            { span: 6, fields: [{ name: 'serviceProvider', label: 'Service Provider', type: 'text', required: false, helpText: 'Provider name', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'providerReference', label: 'Provider Reference', type: 'text', required: false, helpText: 'Invoice / job card number', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'cost', label: 'Cost', type: 'number', required: false, helpText: 'Service cost amount', placeholder: 'Auto-populated' },
                    { name: 'approvalStatus', label: 'Approval Status', type: 'text', required: false, helpText: 'Current approval status', placeholder: 'Auto-populated' }
                ]
            }
        ]
    },

    // ─── ASSET REPAIRS & MAINTENANCE ────────────────────────────────────────────
    {
        id: 'asset-rnm',
        moduleKey: 'Operations',
        contextKey: 'AssetRnM',
        displayName: 'Asset Repairs & Maintenance',
        description: 'Template for ad-hoc asset repair and maintenance action items that require Procurement approval. Unlike scheduled Asset Service items, these are for unexpected breakdowns, damage, or urgent repair needs that arise outside the normal service schedule.',
        icon: FaWrench,
        color: '#d35400',
        suggestedMeta: {
            templateKeyPrefix: 'Operations-AssetRnM',
            displayNameDefault: 'Asset Repair & Maintenance Action Item',
            descriptionDefault: 'Template for ad-hoc asset repairs requiring procurement approval',
            defaultPriority: 'High',
            defaultDueDays: 5,
            department: 'Operations',
            icon: 'fa-wrench',
            color: '#d35400',
            tags: 'asset,repair,maintenance,procurement'
        },
        sections: [
            {
                title: 'Asset Information',
                columns: 1,
                fields: [
                    { name: 'assetName', label: 'Asset Name', type: 'text', required: true, helpText: 'Name of the asset that needs repair or maintenance', placeholder: 'Auto-populated from asset record' },
                    {
                        type: 'row', name: 'row_rnm_ids', label: 'Asset Identifiers',
                        columns: [
                            { span: 6, fields: [{ name: 'assetTag', label: 'Asset Tag', type: 'text', required: false, helpText: 'The asset tag or barcode number used to identify this asset', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'serialNumber', label: 'Serial Number', type: 'text', required: false, helpText: 'Manufacturer serial number', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    {
                        type: 'row', name: 'row_rnm_class', label: 'Classification',
                        columns: [
                            { span: 6, fields: [{ name: 'assetCategory', label: 'Category', type: 'text', required: false, helpText: 'The asset category (e.g. HVAC, Electrical, Plumbing)', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'location', label: 'Location', type: 'text', required: false, helpText: 'Where the asset is located within the property', placeholder: 'Auto-populated' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Repair Details',
                columns: 1,
                fields: [
                    { name: 'issueDescription', label: 'Issue Description', type: 'textarea', required: true, helpText: 'Describe the problem — what is broken, damaged, or malfunctioning and what impact it is having', placeholder: 'Describe the issue in detail...' },
                    {
                        type: 'row', name: 'row_rnm_type', label: 'Repair Type',
                        columns: [
                            { span: 6, fields: [{ name: 'repairType', label: 'Repair Type', type: 'select', required: false, helpText: 'The type of repair or maintenance needed', options: ['Emergency Repair', 'Breakdown Repair', 'Preventive Maintenance', 'Replacement', 'Refurbishment', 'Other'] }] },
                            { span: 6, fields: [{ name: 'estimatedCost', label: 'Estimated Cost', type: 'number', required: false, helpText: 'Approximate cost of the repair (used for procurement approval)', placeholder: 'R 0.00' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Procurement & Approval',
                columns: 1,
                fields: [
                    { name: 'preferredVendor', label: 'Preferred Vendor / Service Provider', type: 'text', required: false, helpText: 'If you have a preferred service provider for this repair, enter their name here', placeholder: 'Enter vendor name if known' },
                    { name: 'approvalStatus', label: 'Approval Status', type: 'text', required: false, helpText: 'Current procurement approval status — this is updated as the item moves through the approval process', placeholder: 'Auto-populated' },
                    { name: 'quoteReference', label: 'Quote Reference', type: 'text', required: false, helpText: 'Reference number for the vendor quote (if available)', placeholder: 'Enter quote reference' }
                ]
            }
        ]
    },

    // ─── VENDOR APPLICATION ─────────────────────────────────────────────────────
    {
        id: 'vendor-application',
        moduleKey: 'Procurement',
        contextKey: 'VendorApplication',
        displayName: 'Vendor Application',
        description: 'Template for action items related to processing new vendor applications and onboarding. Tracks the full vendor approval lifecycle from initial application submission through to final onboarding and system setup.',
        icon: FaUserPlus,
        color: '#8e44ad',
        suggestedMeta: {
            templateKeyPrefix: 'Procurement-VendorApplication',
            displayNameDefault: 'Vendor Application Action Item',
            descriptionDefault: 'Template for new vendor application and onboarding action items',
            defaultPriority: 'Medium',
            defaultDueDays: 14,
            department: 'Procurement',
            icon: 'fa-user-plus',
            color: '#8e44ad',
            tags: 'vendor,application,onboarding,procurement'
        },
        sections: [
            {
                title: 'Vendor Details',
                columns: 1,
                fields: [
                    { name: 'vendorName', label: 'Vendor / Company Name', type: 'text', required: true, helpText: 'The registered name of the company applying to become a vendor', placeholder: 'Auto-populated from application' },
                    {
                        type: 'row', name: 'row_vendor_contact', label: 'Contact',
                        columns: [
                            { span: 6, fields: [{ name: 'contactPerson', label: 'Contact Person', type: 'text', required: false, helpText: 'The primary contact person at the vendor company', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'contactEmail', label: 'Contact Email', type: 'text', required: false, helpText: 'Email address for vendor communication', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'serviceType', label: 'Service Type / Category', type: 'text', required: false, helpText: 'The type of service the vendor provides (e.g. Cleaning, Security, HVAC, Electrical)', placeholder: 'Auto-populated from application' }
                ]
            },
            {
                title: 'Application Review',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_vendor_docs', label: 'Documentation',
                        columns: [
                            { span: 6, fields: [{ name: 'bbbeeLevel', label: 'B-BBEE Level', type: 'text', required: false, helpText: 'The vendor\'s B-BBEE compliance level', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'taxCompliance', label: 'Tax Compliance', type: 'select', required: false, helpText: 'Has the vendor provided a valid tax compliance certificate?', options: ['Pending', 'Received', 'Valid', 'Expired', 'Not Submitted'] }] }
                        ]
                    },
                    { name: 'applicationNotes', label: 'Application Notes', type: 'textarea', required: false, helpText: 'Any notes about the vendor application, special conditions, or concerns', placeholder: 'Enter review notes...' },
                    { name: 'approvalStatus', label: 'Approval Status', type: 'select', required: false, helpText: 'Current status of the vendor application in the approval process', options: ['Submitted', 'Under Review', 'Approved', 'Rejected', 'On Hold', 'Additional Info Required'] }
                ]
            }
        ]
    },

    // ─── PROCUREMENT PACK ───────────────────────────────────────────────────────
    {
        id: 'procurement-pack',
        moduleKey: 'Procurement',
        contextKey: 'ProcurementPack',
        displayName: 'Procurement Pack',
        description: 'Template for action items related to procurement package reviews and approvals. A procurement pack bundles all the documentation needed for a purchase decision — quotes, specifications, and approval sign-offs. This template tracks the entire review process.',
        icon: FaShoppingCart,
        color: '#9b59b6',
        suggestedMeta: {
            templateKeyPrefix: 'Procurement-ProcurementPack',
            displayNameDefault: 'Procurement Pack Action Item',
            descriptionDefault: 'Template for procurement package review and approval action items',
            defaultPriority: 'Medium',
            defaultDueDays: 10,
            department: 'Procurement',
            icon: 'fa-shopping-cart',
            color: '#9b59b6',
            tags: 'procurement,pack,approval,quotes,purchasing'
        },
        sections: [
            {
                title: 'Procurement Details',
                columns: 1,
                fields: [
                    { name: 'packTitle', label: 'Procurement Pack Title', type: 'text', required: true, helpText: 'A descriptive title for this procurement pack (e.g. "HVAC Replacement - Building A")', placeholder: 'Auto-populated or enter title' },
                    { name: 'packDescription', label: 'Description', type: 'textarea', required: false, helpText: 'What is being procured and why — include background, scope of work, and any urgency factors', placeholder: 'Describe the procurement requirement...' },
                    {
                        type: 'row', name: 'row_proc_values', label: 'Financial',
                        columns: [
                            { span: 6, fields: [{ name: 'estimatedValue', label: 'Estimated Value', type: 'number', required: false, helpText: 'Estimated total cost of the procurement', placeholder: 'R 0.00' }] },
                            { span: 6, fields: [{ name: 'budgetCode', label: 'Budget Code', type: 'text', required: false, helpText: 'The budget or cost centre code for this procurement', placeholder: 'Enter budget code' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Quotes & Vendors',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_proc_quotes', label: 'Quotes',
                        columns: [
                            { span: 6, fields: [{ name: 'numberOfQuotes', label: 'Number of Quotes', type: 'number', required: false, helpText: 'How many vendor quotes are included in this procurement pack', placeholder: '0' }] },
                            { span: 6, fields: [{ name: 'recommendedVendor', label: 'Recommended Vendor', type: 'text', required: false, helpText: 'The vendor recommended based on the quote evaluation', placeholder: 'Enter vendor name' }] }
                        ]
                    },
                    { name: 'evaluationNotes', label: 'Evaluation Notes', type: 'textarea', required: false, helpText: 'Summary of the quote evaluation and the reason for the recommendation', placeholder: 'Enter evaluation summary...' }
                ]
            },
            {
                title: 'Approval',
                columns: 1,
                fields: [
                    { name: 'approvalLevel', label: 'Approval Level Required', type: 'select', required: false, helpText: 'The level of approval required based on the procurement value', options: ['Centre Manager', 'Regional Manager', 'Operations Director', 'Finance Director', 'CEO'] },
                    { name: 'approvalStatus', label: 'Approval Status', type: 'select', required: false, helpText: 'Current status of the procurement pack in the approval process', options: ['Draft', 'Submitted', 'Under Review', 'Approved', 'Rejected', 'Revision Requested'] }
                ]
            }
        ]
    },

    // ─── PAYMENT TRACKERS ───────────────────────────────────────────────────────
    {
        id: 'payment-trackers',
        moduleKey: 'Finance',
        contextKey: 'PaymentTrackers',
        displayName: 'Payment Trackers',
        description: 'Template for action items that track payments, invoices, and financial follow-ups. Use this for any financial task that requires tracking — overdue invoices, payment processing, reconciliation queries, or budget-related follow-ups.',
        icon: FaMoneyBillWave,
        color: '#27ae60',
        suggestedMeta: {
            templateKeyPrefix: 'Finance-PaymentTrackers',
            displayNameDefault: 'Payment Tracker Action Item',
            descriptionDefault: 'Template for payment tracking and financial follow-up action items',
            defaultPriority: 'Medium',
            defaultDueDays: 7,
            department: 'Finance',
            icon: 'fa-money-bill-wave',
            color: '#27ae60',
            tags: 'payment,invoice,finance,tracking'
        },
        sections: [
            {
                title: 'Payment Details',
                columns: 1,
                fields: [
                    { name: 'paymentDescription', label: 'Payment Description', type: 'text', required: true, helpText: 'What is this payment for? (e.g. "Monthly cleaning invoice - Property X")', placeholder: 'Describe the payment' },
                    {
                        type: 'row', name: 'row_pay_amounts', label: 'Amounts',
                        columns: [
                            { span: 6, fields: [{ name: 'invoiceAmount', label: 'Invoice Amount', type: 'number', required: false, helpText: 'The total amount on the invoice or payment request', placeholder: 'R 0.00' }] },
                            { span: 6, fields: [{ name: 'invoiceNumber', label: 'Invoice Number', type: 'text', required: false, helpText: 'The vendor invoice number or internal reference', placeholder: 'Enter invoice number' }] }
                        ]
                    },
                    {
                        type: 'row', name: 'row_pay_dates', label: 'Dates',
                        columns: [
                            { span: 6, fields: [{ name: 'invoiceDate', label: 'Invoice Date', type: 'date', required: false, helpText: 'The date on the invoice', placeholder: '' }] },
                            { span: 6, fields: [{ name: 'paymentDueDate', label: 'Payment Due Date', type: 'date', required: false, helpText: 'When the payment is due to be made', placeholder: '' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Vendor & Property',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_pay_vendor', label: 'Vendor',
                        columns: [
                            { span: 6, fields: [{ name: 'vendorName', label: 'Vendor Name', type: 'text', required: false, helpText: 'The vendor or supplier to be paid', placeholder: 'Auto-populated if linked' }] },
                            { span: 6, fields: [{ name: 'propertyName', label: 'Property', type: 'text', required: false, helpText: 'The property this payment relates to (if applicable)', placeholder: 'Auto-populated if linked' }] }
                        ]
                    },
                    { name: 'costCentre', label: 'Cost Centre / Budget Code', type: 'text', required: false, helpText: 'The cost centre or budget code this payment should be allocated to', placeholder: 'Enter cost centre' }
                ]
            },
            {
                title: 'Payment Status',
                columns: 1,
                fields: [
                    { name: 'paymentStatus', label: 'Payment Status', type: 'select', required: false, helpText: 'The current status of this payment', options: ['Pending Approval', 'Approved', 'Processing', 'Paid', 'Overdue', 'Disputed', 'On Hold'] },
                    { name: 'paymentNotes', label: 'Payment Notes', type: 'textarea', required: false, helpText: 'Any additional notes about this payment — special instructions, query details, or reconciliation notes', placeholder: 'Enter notes...' }
                ]
            }
        ]
    },

    // ─── LEASE RENEWAL ──────────────────────────────────────────────────────────
    {
        id: 'lease-renewal',
        moduleKey: 'Leasing',
        contextKey: 'LeaseRenewal',
        displayName: 'Lease Renewal',
        description: 'Template for action items related to the lease renewal process. Tracks the full lifecycle from initial renewal notice through to negotiation, documentation, and final execution. Can also be used for contract expiry reminders generated by the system.',
        icon: FaFileSignature,
        color: '#2980b9',
        suggestedMeta: {
            templateKeyPrefix: 'Leasing-LeaseRenewal',
            displayNameDefault: 'Lease Renewal Action Item',
            descriptionDefault: 'Template for lease renewal and contract expiry action items',
            defaultPriority: 'Medium',
            defaultDueDays: 30,
            department: 'Leasing',
            icon: 'fa-file-signature',
            color: '#2980b9',
            tags: 'lease,renewal,contract,tenant,expiry'
        },
        sections: [
            {
                title: 'Lease Details',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_lease_tenant', label: 'Tenant & Property',
                        columns: [
                            { span: 6, fields: [{ name: 'tenantName', label: 'Tenant Name', type: 'text', required: true, helpText: 'The name of the tenant whose lease is up for renewal', placeholder: 'Auto-populated if linked' }] },
                            { span: 6, fields: [{ name: 'propertyName', label: 'Property', type: 'text', required: false, helpText: 'The property where the lease is held', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    {
                        type: 'row', name: 'row_lease_unit', label: 'Unit & Lease No',
                        columns: [
                            { span: 6, fields: [{ name: 'unitNumber', label: 'Unit / Shop Number', type: 'text', required: false, helpText: 'The specific unit or shop number under this lease', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'leaseNumber', label: 'Lease Number', type: 'text', required: false, helpText: 'The internal lease reference number', placeholder: 'Auto-populated' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Renewal Timeline',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_lease_dates', label: 'Key Dates',
                        columns: [
                            { span: 6, fields: [{ name: 'leaseExpiryDate', label: 'Lease Expiry Date', type: 'date', required: false, helpText: 'When the current lease expires — this drives the urgency of the renewal process', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'renewalDeadline', label: 'Renewal Decision Deadline', type: 'date', required: false, helpText: 'The date by which a renewal decision must be made (usually 60-90 days before expiry)', placeholder: '' }] }
                        ]
                    },
                    { name: 'renewalStatus', label: 'Renewal Status', type: 'select', required: false, helpText: 'Where is this lease renewal in the process?', options: ['Notice Sent', 'Awaiting Tenant Response', 'In Negotiation', 'Terms Agreed', 'Documentation in Progress', 'Executed', 'Not Renewing', 'Expired'] }
                ]
            },
            {
                title: 'Negotiation & Terms',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_lease_terms', label: 'Proposed Terms',
                        columns: [
                            { span: 6, fields: [{ name: 'proposedRental', label: 'Proposed Rental', type: 'number', required: false, helpText: 'The proposed monthly rental amount for the new lease term', placeholder: 'R 0.00' }] },
                            { span: 6, fields: [{ name: 'proposedTerm', label: 'Proposed Lease Term', type: 'text', required: false, helpText: 'The proposed duration of the new lease (e.g. "36 months")', placeholder: 'e.g. 36 months' }] }
                        ]
                    },
                    { name: 'negotiationNotes', label: 'Negotiation Notes', type: 'textarea', required: false, helpText: 'Notes from renewal negotiations — special conditions, tenant requests, or escalation points', placeholder: 'Enter negotiation details...' }
                ]
            }
        ]
    },

    // ─── SUPPORT TICKET ─────────────────────────────────────────────────────────
    {
        id: 'support-ticket',
        moduleKey: 'IT',
        contextKey: 'SupportTicket',
        displayName: 'Support Ticket',
        description: 'Template for IT support requests and technical assistance tickets. Used when staff need help with hardware, software, access issues, or any other IT-related problem. The system automatically generates a ticket reference number (e.g. SUP-00142) for tracking.',
        icon: FaHeadset,
        color: '#3498db',
        suggestedMeta: {
            templateKeyPrefix: 'IT-SupportTicket',
            displayNameDefault: 'IT Support Ticket',
            descriptionDefault: 'Template for IT support request action items',
            defaultPriority: 'Medium',
            defaultDueDays: 3,
            department: 'IT',
            icon: 'fa-headset',
            color: '#3498db',
            tags: 'it,support,ticket,helpdesk'
        },
        sections: [
            {
                title: 'Issue Details',
                columns: 1,
                fields: [
                    { name: 'issueSummary', label: 'Issue Summary', type: 'text', required: true, helpText: 'A brief one-line description of the problem (e.g. "Cannot access shared drive", "Printer not working")', placeholder: 'Describe the issue briefly' },
                    { name: 'issueDescription', label: 'Full Description', type: 'textarea', required: false, helpText: 'Provide as much detail as possible — when did the issue start, what error messages are you seeing, what have you already tried?', placeholder: 'Describe the issue in detail...' },
                    {
                        type: 'row', name: 'row_ticket_class', label: 'Classification',
                        columns: [
                            { span: 6, fields: [{ name: 'issueCategory', label: 'Category', type: 'select', required: false, helpText: 'What type of IT issue is this?', options: ['Hardware', 'Software', 'Network', 'Email', 'Access / Permissions', 'Printing', 'Phone / VoIP', 'Other'] }] },
                            { span: 6, fields: [{ name: 'impactLevel', label: 'Impact', type: 'select', required: false, helpText: 'How many people are affected by this issue?', options: ['Individual', 'Team', 'Department', 'Organisation-wide'] }] }
                        ]
                    }
                ]
            },
            {
                title: 'Requester & Equipment',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_ticket_requester', label: 'Requester',
                        columns: [
                            { span: 6, fields: [{ name: 'requesterName', label: 'Requester Name', type: 'text', required: false, helpText: 'Who is reporting this issue? (auto-filled with logged-in user)', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'requesterLocation', label: 'Location / Property', type: 'text', required: false, helpText: 'Where is the requester located?', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'affectedDevice', label: 'Affected Device / System', type: 'text', required: false, helpText: 'The specific device, computer name, or system that is affected (e.g. "Laptop - LAPTOP-CM01", "Sage Pastel")', placeholder: 'Enter device or system name' }
                ]
            },
            {
                title: 'Resolution',
                columns: 1,
                fields: [
                    { name: 'resolutionNotes', label: 'Resolution Notes', type: 'textarea', required: false, helpText: 'How the issue was resolved — this is filled in by the IT technician when closing the ticket', placeholder: 'Enter resolution details...' },
                    { name: 'rootCause', label: 'Root Cause', type: 'select', required: false, helpText: 'What was the underlying cause of the issue?', options: ['Hardware Failure', 'Software Bug', 'Configuration Error', 'User Error', 'Network Issue', 'Security Issue', 'Third-Party Service', 'Other'] }
                ]
            }
        ]
    },

    // ─── CHANGE REQUEST ─────────────────────────────────────────────────────────
    {
        id: 'change-request',
        moduleKey: 'IT',
        contextKey: 'ChangeRequest',
        displayName: 'Change Request',
        description: 'Template for IT infrastructure or system change requests. Any planned change to IT systems — server upgrades, software deployments, network reconfiguration, or security policy changes — should go through the change request process to minimise risk and ensure proper approval.',
        icon: FaExchangeAlt,
        color: '#f39c12',
        suggestedMeta: {
            templateKeyPrefix: 'IT-ChangeRequest',
            displayNameDefault: 'IT Change Request',
            descriptionDefault: 'Template for IT change request action items',
            defaultPriority: 'Medium',
            defaultDueDays: 7,
            department: 'IT',
            icon: 'fa-exchange-alt',
            color: '#f39c12',
            tags: 'it,change,request,infrastructure'
        },
        sections: [
            {
                title: 'Change Details',
                columns: 1,
                fields: [
                    { name: 'changeSummary', label: 'Change Summary', type: 'text', required: true, helpText: 'A brief description of the proposed change (e.g. "Upgrade mail server to Exchange 2025")', placeholder: 'Describe the change briefly' },
                    { name: 'changeDescription', label: 'Full Description', type: 'textarea', required: false, helpText: 'Detailed description of what will change, why it is needed, and the expected outcome', placeholder: 'Describe the change in detail...' },
                    {
                        type: 'row', name: 'row_change_class', label: 'Classification',
                        columns: [
                            { span: 6, fields: [{ name: 'changeType', label: 'Change Type', type: 'select', required: false, helpText: 'What type of change is this?', options: ['Standard', 'Normal', 'Emergency', 'Major'] }] },
                            { span: 6, fields: [{ name: 'riskLevel', label: 'Risk Level', type: 'select', required: false, helpText: 'What is the risk if something goes wrong during this change?', options: ['Low', 'Medium', 'High', 'Critical'] }] }
                        ]
                    }
                ]
            },
            {
                title: 'Impact & Schedule',
                columns: 1,
                fields: [
                    { name: 'impactAssessment', label: 'Impact Assessment', type: 'textarea', required: false, helpText: 'Who and what will be affected by this change? Will there be any downtime? List all affected systems and users.', placeholder: 'Describe the impact...' },
                    {
                        type: 'row', name: 'row_change_dates', label: 'Schedule',
                        columns: [
                            { span: 6, fields: [{ name: 'plannedStartDate', label: 'Planned Start', type: 'datetime', required: false, helpText: 'When the change implementation will begin', placeholder: '' }] },
                            { span: 6, fields: [{ name: 'plannedEndDate', label: 'Planned End', type: 'datetime', required: false, helpText: 'When the change implementation is expected to be complete', placeholder: '' }] }
                        ]
                    },
                    { name: 'rollbackPlan', label: 'Rollback Plan', type: 'textarea', required: false, helpText: 'What is the plan if the change fails and needs to be reversed? This is critical for risk management.', placeholder: 'Describe the rollback procedure...' }
                ]
            },
            {
                title: 'Approval & Review',
                columns: 1,
                fields: [
                    { name: 'approvalStatus', label: 'Approval Status', type: 'select', required: false, helpText: 'Current status of the change request in the approval process', options: ['Draft', 'Submitted', 'Under Review', 'Approved', 'Rejected', 'Implemented', 'Rolled Back'] },
                    { name: 'reviewNotes', label: 'Review Notes', type: 'textarea', required: false, helpText: 'Notes from the change review board or approver', placeholder: 'Enter review notes...' }
                ]
            }
        ]
    },

    // ─── IT ONBOARDING ──────────────────────────────────────────────────────────
    {
        id: 'it-onboarding',
        moduleKey: 'IT',
        contextKey: 'Onboarding',
        displayName: 'IT Onboarding',
        description: 'Template for action items related to new employee IT setup. Covers everything a new joiner needs — user accounts, email setup, computer provisioning, software installation, access permissions, and orientation. Ensures nothing is missed when onboarding new staff.',
        icon: FaUserCheck,
        color: '#1abc9c',
        suggestedMeta: {
            templateKeyPrefix: 'IT-Onboarding',
            displayNameDefault: 'IT Onboarding Action Item',
            descriptionDefault: 'Template for new employee IT onboarding action items',
            defaultPriority: 'High',
            defaultDueDays: 5,
            department: 'IT',
            icon: 'fa-user-check',
            color: '#1abc9c',
            tags: 'it,onboarding,new-employee,setup'
        },
        sections: [
            {
                title: 'New Employee Details',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_onboard_emp', label: 'Employee',
                        columns: [
                            { span: 6, fields: [{ name: 'employeeName', label: 'Employee Name', type: 'text', required: true, helpText: 'Full name of the new employee being onboarded', placeholder: 'Enter employee name' }] },
                            { span: 6, fields: [{ name: 'jobTitle', label: 'Job Title', type: 'text', required: false, helpText: 'The new employee\'s job title — this helps determine what access and software they need', placeholder: 'Enter job title' }] }
                        ]
                    },
                    {
                        type: 'row', name: 'row_onboard_dept', label: 'Department & Location',
                        columns: [
                            { span: 6, fields: [{ name: 'department', label: 'Department', type: 'text', required: false, helpText: 'Which department the employee is joining', placeholder: 'Enter department' }] },
                            { span: 6, fields: [{ name: 'officeLocation', label: 'Office / Property', type: 'text', required: false, helpText: 'Where the employee will be based', placeholder: 'Enter location' }] }
                        ]
                    },
                    { name: 'startDate', label: 'Start Date', type: 'date', required: false, helpText: 'When the new employee starts — IT setup should be completed before this date', placeholder: '' },
                    { name: 'managerName', label: 'Line Manager', type: 'text', required: false, helpText: 'The new employee\'s direct manager (for access approval and notification)', placeholder: 'Enter manager name' }
                ]
            },
            {
                title: 'IT Setup Checklist',
                columns: 1,
                fields: [
                    { name: 'accountsRequired', label: 'Accounts Required', type: 'textarea', required: false, helpText: 'List all accounts needed: AD/domain account, email, VPN, software logins (Sage, CapTrac, etc.)', placeholder: 'e.g. Domain account, Email, VPN, Sage Pastel, CapTrac' },
                    { name: 'equipmentRequired', label: 'Equipment Required', type: 'textarea', required: false, helpText: 'List all hardware needed: laptop/desktop, monitors, phone, headset, access card, etc.', placeholder: 'e.g. Laptop, 2x monitors, headset, access card' },
                    { name: 'softwareRequired', label: 'Software Required', type: 'textarea', required: false, helpText: 'List all software that needs to be installed and licenced', placeholder: 'e.g. Microsoft 365, Adobe Acrobat, Sage Pastel' },
                    { name: 'specialAccess', label: 'Special Access Requirements', type: 'textarea', required: false, helpText: 'Any special access permissions, shared drives, distribution groups, or security groups the employee needs', placeholder: 'e.g. Finance shared drive, Management distribution list' }
                ]
            }
        ]
    },

    // ─── IT OFFBOARDING ─────────────────────────────────────────────────────────
    {
        id: 'it-offboarding',
        moduleKey: 'IT',
        contextKey: 'Offboarding',
        displayName: 'IT Offboarding',
        description: 'Template for action items related to departing employee IT deprovisioning. Covers disabling accounts, revoking access, retrieving equipment, forwarding emails, and archiving data. Critical for security — ensures no access is left active after an employee leaves.',
        icon: FaUserTimes,
        color: '#c0392b',
        suggestedMeta: {
            templateKeyPrefix: 'IT-Offboarding',
            displayNameDefault: 'IT Offboarding Action Item',
            descriptionDefault: 'Template for departing employee IT offboarding action items',
            defaultPriority: 'High',
            defaultDueDays: 1,
            department: 'IT',
            icon: 'fa-user-times',
            color: '#c0392b',
            tags: 'it,offboarding,departing,access-removal,security'
        },
        sections: [
            {
                title: 'Departing Employee Details',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_offboard_emp', label: 'Employee',
                        columns: [
                            { span: 6, fields: [{
                                name: 'employeeName', label: 'Employee Name', type: 'entra-user-selector', required: true,
                                helpText: 'Start typing to search Microsoft 365 — selecting a user auto-fills job title, department, manager, and device',
                                placeholder: 'Search by name or email...',
                                autoPopulate: {
                                    jobTitle: 'jobTitle',
                                    department: 'department',
                                    managerName: 'managerName',
                                    primaryDeviceHostname: 'deviceHostname',
                                    email: 'employeeEmail'
                                }
                            }] },
                            { span: 6, fields: [{ name: 'jobTitle', label: 'Job Title', type: 'text', required: false, helpText: 'Auto-filled from Microsoft 365 when an employee is selected', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    {
                        type: 'row', name: 'row_offboard_dates', label: 'Key Dates',
                        columns: [
                            { span: 6, fields: [{ name: 'lastWorkingDay', label: 'Last Working Day', type: 'date', required: false, helpText: 'The employee\'s last day — automations trigger at COB on this date', placeholder: '' }] },
                            { span: 6, fields: [{ name: 'department', label: 'Department', type: 'text', required: false, helpText: 'Auto-filled from Microsoft 365', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    {
                        type: 'row', name: 'row_offboard_mgr', label: 'Manager & Device',
                        columns: [
                            { span: 6, fields: [{ name: 'managerName', label: 'Line Manager', type: 'text', required: false, helpText: 'Auto-filled from Microsoft 365 (the employee\'s manager for email forwarding and data handover)', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'deviceHostname', label: 'Computer Hostname / Asset Tag', type: 'text', required: false, helpText: 'Auto-filled from Intune (the hostname of the employee\'s primary device)', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'employeeEmail', label: 'Employee Email', type: 'email', required: false, helpText: 'Auto-filled from Microsoft 365', placeholder: 'Auto-populated' }
                ]
            },
            {
                title: 'Automation Configuration',
                columns: 1,
                fields: [
                    { name: 'emailCleanupRole', label: 'Email Cleanup Role', type: 'role-selector', required: false, helpText: 'Select the role whose emails should be deleted from the departing employee\'s mailbox (e.g. Human Resources). The departing user is skipped if they are in this role.' },
                    {
                        type: 'info-alert', name: 'automation_info', label: '', alertVariant: 'info',
                        content: 'Workflow automations configured on this template will run automatically at COB on the Last Working Day. This includes disabling the user\'s Microsoft 365 and CapTrac accounts, and deleting emails to/from users in the selected role above.'
                    }
                ]
            },
            {
                title: 'Access Removal Checklist',
                columns: 1,
                fields: [
                    { name: 'accountsToDisable', label: 'Accounts to Disable', type: 'textarea', required: false, helpText: 'List all accounts that need to be disabled: AD account, email, VPN, all software logins', placeholder: 'e.g. AD account, Email, VPN, Sage, CapTrac, SharePoint' },
                    { name: 'emailForwarding', label: 'Email Forwarding', type: 'text', required: false, helpText: 'Who should receive this person\'s emails after they leave? (Email forwarding is typically set up for 30-90 days)', placeholder: 'e.g. Forward to manager@company.co.za for 60 days' },
                    { name: 'dataHandover', label: 'Data Handover', type: 'textarea', required: false, helpText: 'What needs to happen with the employee\'s files, emails, and data? (Archive, transfer to manager, delete)', placeholder: 'e.g. Archive mailbox, transfer Documents to manager' }
                ]
            },
            {
                title: 'Equipment Return',
                columns: 1,
                fields: [
                    { name: 'equipmentToReturn', label: 'Equipment to Return', type: 'textarea', required: false, helpText: 'List all company equipment the employee needs to return: laptop, phone, access card, keys, etc.', placeholder: 'e.g. Laptop (LAPTOP-JD01), iPhone, access card #1234' },
                    { name: 'returnStatus', label: 'Return Status', type: 'select', required: false, helpText: 'Has all equipment been returned?', options: ['Pending', 'Partial Return', 'All Returned', 'Outstanding Items'] }
                ]
            }
        ]
    },

    // ─── SOFTWARE DEV REQUEST ───────────────────────────────────────────────────
    {
        id: 'software-dev-request',
        moduleKey: 'IT',
        contextKey: 'SoftwareDevRequest',
        displayName: 'Software Dev Request',
        description: 'Template for software development requests including new feature requests, enhancements to existing systems, and bug fix reports. Helps the development team triage, estimate, and track development work from request through to deployment.',
        icon: FaCode,
        color: '#2ecc71',
        suggestedMeta: {
            templateKeyPrefix: 'IT-SoftwareDevRequest',
            displayNameDefault: 'Software Development Request',
            descriptionDefault: 'Template for software development request action items',
            defaultPriority: 'Medium',
            defaultDueDays: 14,
            department: 'IT',
            icon: 'fa-code',
            color: '#2ecc71',
            tags: 'it,software,development,feature,bug'
        },
        sections: [
            {
                title: 'Request Details',
                columns: 1,
                fields: [
                    { name: 'requestTitle', label: 'Request Title', type: 'text', required: true, helpText: 'A clear, concise title for this development request (e.g. "Add export to PDF on Lease Report")', placeholder: 'Enter a descriptive title' },
                    { name: 'requestDescription', label: 'Full Description', type: 'textarea', required: true, helpText: 'Describe what you need in detail — what should the feature do, or what is the bug and how to reproduce it? The more detail you provide, the faster the team can act on it.', placeholder: 'Describe the requirement or issue in detail...' },
                    {
                        type: 'row', name: 'row_dev_class', label: 'Classification',
                        columns: [
                            { span: 6, fields: [{ name: 'requestType', label: 'Request Type', type: 'select', required: false, helpText: 'Is this a new feature, an enhancement to something existing, or a bug fix?', options: ['New Feature', 'Enhancement', 'Bug Fix', 'Performance Issue', 'Security Issue', 'Data Request', 'Other'] }] },
                            { span: 6, fields: [{ name: 'affectedSystem', label: 'Affected System', type: 'text', required: false, helpText: 'Which system or module is this request for? (e.g. "CapTrac - Leasing Module", "Sage Integration")', placeholder: 'Enter system name' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Business Impact',
                columns: 1,
                fields: [
                    { name: 'businessJustification', label: 'Business Justification', type: 'textarea', required: false, helpText: 'Why is this needed? What business problem does it solve? This helps the team prioritise against other requests.', placeholder: 'Explain the business need...' },
                    {
                        type: 'row', name: 'row_dev_impact', label: 'Impact & Urgency',
                        columns: [
                            { span: 6, fields: [{ name: 'businessImpact', label: 'Business Impact', type: 'select', required: false, helpText: 'How significantly does this affect business operations?', options: ['Low - Nice to have', 'Medium - Inconvenient workaround exists', 'High - Significant impact', 'Critical - Work stoppage'] }] },
                            { span: 6, fields: [{ name: 'requestedDeadline', label: 'Requested Deadline', type: 'date', required: false, helpText: 'Is there a specific deadline this needs to be completed by?', placeholder: '' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Development Tracking',
                columns: 1,
                fields: [
                    { name: 'devEstimate', label: 'Development Estimate', type: 'text', required: false, helpText: 'Estimated effort to complete this request (filled in by the development team)', placeholder: 'e.g. 2 days, 1 sprint' },
                    { name: 'devStatus', label: 'Development Status', type: 'select', required: false, helpText: 'Current status of this development request', options: ['New', 'Triaged', 'In Backlog', 'In Progress', 'In Testing', 'Ready for Deployment', 'Deployed', 'Rejected'] },
                    { name: 'devNotes', label: 'Development Notes', type: 'textarea', required: false, helpText: 'Technical notes from the development team — implementation details, dependencies, or blockers', placeholder: 'Enter development notes...' }
                ]
            }
        ]
    },

    // ─── ROUTER MONITORING ─────────────────────────────────────────────────────
    {
        id: 'router-monitoring',
        moduleKey: 'IT',
        contextKey: 'NetworkMonitoring',
        displayName: 'Router Monitoring',
        description: 'Template for action items created by the Router Monitoring system when network alerts are triggered. Fields are auto-populated from the router configuration and alert data.',
        icon: FaNetworkWired,
        color: '#e67e22',
        suggestedMeta: {
            templateKeyPrefix: 'IT-NetworkMonitoring',
            displayNameDefault: 'Router Alert Action Item',
            descriptionDefault: 'Template for router monitoring alert action items',
            defaultPriority: 'High',
            defaultDueDays: 1,
            department: 'IT',
            icon: 'fa-network-wired',
            color: '#e67e22',
            tags: 'router,network,alert,monitoring'
        },
        sections: [
            {
                title: 'Router Information',
                columns: 1,
                fields: [
                    { name: 'routerName', label: 'Router Name', type: 'text', required: true, helpText: 'Display name of the router', placeholder: 'Auto-populated from router config' },
                    {
                        type: 'row', name: 'row_router_network', label: 'Network',
                        columns: [
                            { span: 6, fields: [{ name: 'ipAddress', label: 'IP Address', type: 'text', required: false, helpText: 'Router IP address and port', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'routerLocation', label: 'Location', type: 'text', required: false, helpText: 'Physical location / property', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'managementInterface', label: 'Management Interface', type: 'text', required: false, helpText: 'Management interface type (e.g. RouterOS API)', placeholder: 'Auto-populated' }
                ]
            },
            {
                title: 'Alert Details',
                columns: 1,
                fields: [
                    { name: 'alertType', label: 'Alert Type', type: 'text', required: true, helpText: 'Type of alert (Availability, CpuThreshold, MemoryThreshold, etc.)', placeholder: 'Auto-populated' },
                    { name: 'alertDescription', label: 'Alert Description', type: 'textarea', required: false, helpText: 'Detailed description of the alert condition', placeholder: 'Auto-populated' },
                    {
                        type: 'row', name: 'row_alert_values', label: 'Threshold Values',
                        columns: [
                            { span: 6, fields: [{ name: 'triggerValue', label: 'Trigger Value', type: 'text', required: false, helpText: 'The value that triggered the alert', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'thresholdValue', label: 'Threshold', type: 'text', required: false, helpText: 'The configured threshold that was exceeded', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'detectedTime', label: 'Detected At', type: 'datetime', required: false, helpText: 'When the alert was detected', placeholder: 'Auto-populated' }
                ]
            },
            {
                title: 'Resolution',
                columns: 1,
                fields: [
                    { name: 'resolutionNotes', label: 'Resolution Notes', type: 'textarea', required: false, helpText: 'Notes about how the issue was resolved', placeholder: 'Enter resolution details...' },
                    { name: 'rootCause', label: 'Root Cause', type: 'select', required: false, helpText: 'Root cause category', options: ['Hardware Failure', 'Software Bug', 'Configuration Error', 'Network Issue', 'Power Outage', 'ISP Issue', 'Capacity Exceeded', 'Security Incident', 'Other'] },
                    { name: 'resolutionTime', label: 'Resolution Time', type: 'datetime', required: false, helpText: 'When the issue was resolved', placeholder: '' }
                ]
            }
        ]
    },

    // ─── PROJECT TASK ───────────────────────────────────────────────────────────
    {
        id: 'project-task',
        moduleKey: 'Projects',
        contextKey: 'ProjectTask',
        displayName: 'Project Task',
        description: 'Template for main project tasks and deliverables managed through the Projects module. Each project task is linked to a project and optionally to a milestone. Use this for tracking significant pieces of work that contribute directly to project completion.',
        icon: FaProjectDiagram,
        color: '#8e44ad',
        suggestedMeta: {
            templateKeyPrefix: 'Projects-ProjectTask',
            displayNameDefault: 'Project Task Action Item',
            descriptionDefault: 'Template for main project task action items',
            defaultPriority: 'Medium',
            defaultDueDays: 14,
            department: '',
            icon: 'fa-project-diagram',
            color: '#8e44ad',
            tags: 'project,task,deliverable,milestone'
        },
        sections: [
            {
                title: 'Task Details',
                columns: 1,
                fields: [
                    { name: 'taskTitle', label: 'Task Title', type: 'text', required: true, helpText: 'The name of this project task — be specific so team members know exactly what needs to be done', placeholder: 'Auto-populated from project' },
                    { name: 'taskDescription', label: 'Description', type: 'textarea', required: false, helpText: 'Detailed description of what this task involves, what the expected deliverable is, and any acceptance criteria', placeholder: 'Describe the task and its deliverables...' },
                    {
                        type: 'row', name: 'row_task_dates', label: 'Timeline',
                        columns: [
                            { span: 6, fields: [{ name: 'startDate', label: 'Start Date', type: 'date', required: false, helpText: 'When work on this task should begin', placeholder: '' }] },
                            { span: 6, fields: [{ name: 'expectedCompletionDate', label: 'Expected Completion', type: 'date', required: false, helpText: 'When this task is expected to be finished', placeholder: '' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Project Context',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_task_project', label: 'Project',
                        columns: [
                            { span: 6, fields: [{ name: 'projectName', label: 'Project Name', type: 'text', required: false, helpText: 'The project this task belongs to', placeholder: 'Auto-populated from project' }] },
                            { span: 6, fields: [{ name: 'milestoneName', label: 'Milestone', type: 'text', required: false, helpText: 'The project milestone this task contributes to (if applicable)', placeholder: 'Auto-populated if linked' }] }
                        ]
                    },
                    {
                        type: 'row', name: 'row_task_assign', label: 'Assignment',
                        columns: [
                            { span: 6, fields: [{ name: 'assignedTo', label: 'Assigned To', type: 'text', required: false, helpText: 'The person responsible for completing this task', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'department', label: 'Department', type: 'text', required: false, helpText: 'The department responsible for this task', placeholder: 'Auto-populated from project' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Progress & Notes',
                columns: 1,
                fields: [
                    { name: 'progressNotes', label: 'Progress Notes', type: 'textarea', required: false, helpText: 'Use this field to track progress updates as work progresses on this task', placeholder: 'Enter progress updates...' },
                    { name: 'blockers', label: 'Blockers / Dependencies', type: 'textarea', required: false, helpText: 'Is anything blocking this task? Does it depend on another task being completed first?', placeholder: 'Describe any blockers or dependencies...' }
                ]
            }
        ]
    },

    // ─── PROJECT SUB-TASK ───────────────────────────────────────────────────────
    {
        id: 'project-subtask',
        moduleKey: 'Projects',
        contextKey: 'ProjectSubTask',
        displayName: 'Project Sub-Task',
        description: 'Template for sub-tasks and smaller work items that sit under a main project task. Sub-tasks break large tasks into manageable pieces that can be assigned to different people or tracked independently. They roll up to their parent task for progress reporting.',
        icon: FaStream,
        color: '#9b59b6',
        suggestedMeta: {
            templateKeyPrefix: 'Projects-ProjectSubTask',
            displayNameDefault: 'Project Sub-Task Action Item',
            descriptionDefault: 'Template for project sub-task action items',
            defaultPriority: 'Medium',
            defaultDueDays: 7,
            department: '',
            icon: 'fa-stream',
            color: '#9b59b6',
            tags: 'project,subtask,work-item'
        },
        sections: [
            {
                title: 'Sub-Task Details',
                columns: 1,
                fields: [
                    { name: 'subTaskTitle', label: 'Sub-Task Title', type: 'text', required: true, helpText: 'A concise name for this sub-task', placeholder: 'Auto-populated from project' },
                    { name: 'subTaskDescription', label: 'Description', type: 'textarea', required: false, helpText: 'What specifically needs to be done for this sub-task', placeholder: 'Describe the sub-task...' },
                    {
                        type: 'row', name: 'row_subtask_dates', label: 'Timeline',
                        columns: [
                            { span: 6, fields: [{ name: 'startDate', label: 'Start Date', type: 'date', required: false, helpText: 'When work on this sub-task should begin', placeholder: '' }] },
                            { span: 6, fields: [{ name: 'expectedCompletionDate', label: 'Expected Completion', type: 'date', required: false, helpText: 'When this sub-task is expected to be finished', placeholder: '' }] }
                        ]
                    }
                ]
            },
            {
                title: 'Parent Task & Context',
                columns: 1,
                fields: [
                    {
                        type: 'row', name: 'row_subtask_parent', label: 'Parent',
                        columns: [
                            { span: 6, fields: [{ name: 'parentTaskTitle', label: 'Parent Task', type: 'text', required: false, helpText: 'The main project task this sub-task belongs to', placeholder: 'Auto-populated' }] },
                            { span: 6, fields: [{ name: 'projectName', label: 'Project Name', type: 'text', required: false, helpText: 'The project this sub-task is part of', placeholder: 'Auto-populated' }] }
                        ]
                    },
                    { name: 'assignedTo', label: 'Assigned To', type: 'text', required: false, helpText: 'The person responsible for this sub-task (can differ from the parent task assignee)', placeholder: 'Auto-populated' }
                ]
            },
            {
                title: 'Completion',
                columns: 1,
                fields: [
                    { name: 'completionNotes', label: 'Completion Notes', type: 'textarea', required: false, helpText: 'Notes about the work done to complete this sub-task', placeholder: 'Enter completion details...' }
                ]
            }
        ]
    }
];

/**
 * Look up a preset by its unique id.
 * @param {string} presetId - The preset identifier
 * @returns {object|undefined} The preset definition or undefined
 */
export const getPresetById = (presetId) =>
    WIZARD_PRESETS.find(p => p.id === presetId);

/**
 * Look up a preset by module + context key pair.
 * @param {string} moduleKey
 * @param {string} contextKey
 * @returns {object|undefined}
 */
export const getPresetByContext = (moduleKey, contextKey) =>
    WIZARD_PRESETS.find(p => p.moduleKey === moduleKey && p.contextKey === contextKey);
